import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { IconComponent, IconName } from '../../icons/icon.component';

@Component({
  selector: 'app-slide-panel',
  standalone: true,
  imports: [CommonModule, ClickOutsideDirective, IconComponent],
  templateUrl: './slide-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 1, 1)',
          style({ transform: 'translateX(100%)', opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export class SlidePanelComponent implements AfterViewInit, OnDestroy {
  @Input() open: boolean = false;
  @Input() title: string = '';
  @Input() showClose: boolean = true;
  @Input() showCopyIcon: boolean = false;
  @Input() panelClass: string = '';
  @Input() headerClass: string = '';
  @Input() contentClass: string = 'p-4';
  @Input() titleIcon: IconName | '' = '';
  @Input() outsideClick: boolean = false;
  @Input() mode: 'overlay' | 'inline' = 'overlay';
  @Input() containerClass: string = '';
  @Input() backdropBlurClass: string = 'backdrop-blur-[2px]';
  @Input() showHistoryIcon: boolean = false;
  /**
   * When `true` (default) and `mode === 'overlay'`, the host
   * `<app-slide-panel>` element is reparented to `document.body` after
   * view init.
   *
   * Why: the panel relies on `position: fixed` to anchor itself to the
   * viewport. When the panel is rendered inside an embedded report on
   * `<app-my-dashboard>`, an ancestor (the `cdkDrag .unified-tile`
   * wrapper / `card-bg isolate` chain / etc.) establishes a new
   * containing block, which clips the panel to the tile instead of
   * letting it overlay the full page. Moving the host to `<body>`
   * guarantees no ancestor can interfere with the fixed-positioning,
   * so the same panel works identically on the standalone page and
   * inside the customized dashboard.
   *
   * Inline-mode panels (e.g. the AI chat sidebar in
   * `private-layout.component.ts`) stay in place because they're
   * intentionally rendered as part of the page's flex layout.
   */
  @Input() teleportToBody: boolean = true;
  @Output() closed = new EventEmitter<void>();
  @Output() copyPrompt = new EventEmitter<void>();
  @Output() historyClicked = new EventEmitter<void>();

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly document = inject(DOCUMENT);

  /** Original parent the host was attached to before teleporting. */
  private originalParent: Node | null = null;
  /** Original next sibling so the host re-attaches at the same slot on destroy. */
  private originalNextSibling: Node | null = null;
  /** Whether the host has been moved to `<body>`. */
  private teleported = false;

  ngAfterViewInit(): void {
    if (!this.teleportToBody || this.mode !== 'overlay') return;
    const host = this.elementRef.nativeElement as HTMLElement;
    if (host.parentNode === this.document.body) return;
    this.originalParent = host.parentNode;
    this.originalNextSibling = host.nextSibling;
    this.document.body.appendChild(host);
    this.teleported = true;
  }

  ngOnDestroy(): void {
    if (!this.teleported) return;
    const host = this.elementRef.nativeElement as HTMLElement;
    // Re-attach to the original parent BEFORE Angular's view removal
    // walks up the registered parent to detach the host. If the host
    // is still parented to <body> at that point, Angular's
    // `removeChild(originalParent, host)` would silently no-op and
    // leak the DOM nodes.
    if (
      this.originalParent &&
      host.parentNode === this.document.body
    ) {
      if (
        this.originalNextSibling &&
        this.originalNextSibling.parentNode === this.originalParent
      ) {
        this.originalParent.insertBefore(host, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(host);
      }
    }
    this.originalParent = null;
    this.originalNextSibling = null;
    this.teleported = false;
  }

  hide() {
    this.open = false;
    this.closed.emit();
  }

  copyEntirePrompt() {
    this.copyPrompt.emit();
  }
}
